import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, Lock } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import PeriodFilter, { applyPeriodFilter } from '../components/PeriodFilter';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#84cc16', '#6366f1'];

const fmt = (n) => (parseFloat(n) || 0).toLocaleString('fr-FR');

export default function TableauProduits({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState([]);
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/financial/dashboard`);
        setCredits(r.data.credits || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => applyPeriodFilter(credits, period), [credits, period]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      const key = r.annonceur || r.categorie || 'Autres';
      map[key] = (map[key] || 0) + (parseFloat(r.montant) || 0);
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filtered]);

  const total = filtered.reduce((s, r) => s + (parseFloat(r.montant) || 0), 0);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="tableau-produits">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate(-1)} variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <TrendingUp className="h-7 w-7 text-green-600" /> Détail des Produits
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                {filtered.length} produit{filtered.length > 1 ? 's' : ''} —{' '}
                <strong>{fmt(total)} FCFA</strong>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PeriodFilter value={period} onChange={setPeriod} color="green" testIdPrefix="produits-period" />
            <Badge variant="outline" className="gap-1 border-gray-300 text-gray-600 bg-gray-50">
              <Lock className="h-3 w-3" /> Lecture seule
            </Badge>
          </div>
        </div>

        {/* Charts + Table */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="shadow-lg">
            <CardHeader className="bg-green-600 text-white">
              <CardTitle>Répartition des produits</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {grouped.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-400 italic">
                  Aucune donnée à afficher
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={grouped}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={110}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {grouped.map((entry, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `${fmt(v)} FCFA`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader className="bg-green-600 text-white">
              <CardTitle>Détail des produits</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-y-auto max-h-[400px]">
                <table className="w-full text-sm">
                  <thead className="bg-green-50 border-b border-green-200 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-green-800 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-green-800 uppercase">Annonceur</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-green-800 uppercase">Catégorie</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-green-800 uppercase">Montant</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-4 text-center text-gray-400 italic">
                          Aucun produit
                        </td>
                      </tr>
                    ) : (
                      filtered
                        .slice()
                        .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                        .map((r, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-700">{r.date || '—'}</td>
                            <td className="px-4 py-2 text-gray-800">{r.annonceur || '—'}</td>
                            <td className="px-4 py-2 text-gray-600">{r.categorie || '—'}</td>
                            <td className="px-4 py-2 text-right font-medium text-green-700">{fmt(r.montant)} FCFA</td>
                          </tr>
                        ))
                    )}
                  </tbody>
                  <tfoot className="bg-green-50 border-t-2 border-green-200">
                    <tr>
                      <td colSpan={3} className="px-4 py-3 font-bold text-green-900 uppercase">Total</td>
                      <td className="px-4 py-3 text-right font-bold text-green-700 text-lg">{fmt(total)} FCFA</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
